import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export interface SignedMandateUpload {
  file: File;
  signatureDate: string;
  confirmed: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short description of the mandate being activated (e.g. holder · IBAN). */
  subtitle?: string;
  /** True when the mandate is already active and this upload only adds a new signed version. */
  isReplacement?: boolean;
  isPending?: boolean;
  onSubmit: (upload: SignedMandateUpload) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Collects the signed document, the ACTUAL signature date and an explicit
 * confirmation before a mandate is activated. Upload alone never activates a
 * mandate — the confirmation is a deliberate human check.
 */
const SignedMandateUploadDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  subtitle,
  isReplacement,
  isPending,
  onSubmit,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [signatureDate, setSignatureDate] = useState<string>(today());
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setFile(null);
      setSignatureDate(today());
      setConfirmed(false);
      setErrors({});
    }
  }, [open]);

  const handleSubmit = () => {
    const next: Record<string, string> = {};
    if (!file) next.file = "Bitte das signierte Dokument auswählen";
    if (!signatureDate) next.signatureDate = "Unterschriftsdatum erforderlich";
    else if (signatureDate > today()) next.signatureDate = "Darf nicht in der Zukunft liegen";
    if (!confirmed) next.confirmed = "Bitte bestätigen";
    setErrors(next);
    if (Object.keys(next).length > 0 || !file) return;
    onSubmit({ file, signatureDate, confirmed });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isReplacement
              ? "Neue signierte Version hochladen"
              : "Signiertes Mandat hochladen & bestätigen"}
          </DialogTitle>
          <DialogDescription className="text-[15px]">
            {isReplacement ? (
              <>
                Ersetzt das aktuell hinterlegte Dokument durch eine neue Version. Die bisherigen
                Versionen bleiben zur Nachvollziehbarkeit erhalten.
                {subtitle ? ` (${subtitle})` : ""}
              </>
            ) : (
              (subtitle ??
              "Laden Sie das unterschriebene Dokument hoch und bestätigen Sie die Prüfung.")
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label className="text-base">Signiertes Dokument</Label>
            <Input
              type="file"
              accept="application/pdf,image/*"
              disabled={isPending}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {errors.file && <p className="text-sm text-destructive">{errors.file}</p>}
          </div>

          <div className="space-y-1.5">
            <Label className="text-base">Unterschrieben am</Label>
            <Input
              type="date"
              max={today()}
              value={signatureDate}
              disabled={isPending}
              onChange={(e) => setSignatureDate(e.target.value)}
            />
            {errors.signatureDate && (
              <p className="text-sm text-destructive">{errors.signatureDate}</p>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-solid border-slate-200 p-3">
            <Checkbox
              checked={confirmed}
              disabled={isPending}
              onCheckedChange={(v) => setConfirmed(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-slate-700">
              Ich bestätige, dass das Dokument lesbar und unterschrieben ist und den eingegebenen
              Bank- und Schuldnerdaten entspricht.
            </span>
          </label>
          {errors.confirmed && <p className="text-sm text-destructive">{errors.confirmed}</p>}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button
            className="bg-blue-darkest text-white hover:bg-blue-darkest/90"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? "Wird gespeichert…" : isReplacement ? "Version hochladen" : "Aktivieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignedMandateUploadDialog;
